// The draft engine (REALTIME.md "one pure engine, three hosts"): draft
// state in, intent and time in, new state and events out. No clocks, no
// I/O, no sport names. The host (Durable Object, simulator, client) decides
// when to call `advance` with the current time.

import { pickAi, type AiManager } from './ai';
import {
  advanceAuction,
  applyAuction,
  auctionNextAt,
  auctionState,
  type AuctionRules,
  type AuctionState,
} from './auction';
import { holderOf, overallOf, ownerOf, totalPicks } from './order';
import { resolvePick, type ResolveInput, type ResolvePlan, type Resolved } from './resolve';
import type { DraftClub, DraftFacts } from './style';
import {
  answerOffer,
  offerTrade,
  sweepOffers,
  undoTrade,
  withdrawOffer,
  type DraftOffer,
  type DraftTrade,
  type OfferEnd,
  type TradeItem,
} from './trades';

export { teamForPick, totalPicks, ownerOf, holderOf, overallOf, pickKey } from './order';

export type DraftType = 'snake' | 'linear' | 'third-round-reversal';

/**
 * What a live pick clock does when it runs out: the league's
 * `draft.whenTheClockExpires`. The queue, then the manager's plan, then the
 * house list; the same without the queue; or nothing at all — the clock
 * reads zero and the room waits for the manager, Sleeper's soft timer
 * (DRAFT_MODES §3.1).
 */
export type ClockExpiry = 'queue-then-rankings' | 'rankings' | 'off';

/**
 * How long a seat on auto waits once its turn comes before the room picks for
 * it. Long enough that the board visibly moves to the seat before it fills,
 * short enough that nobody notices a seat that is drafting by itself.
 */
export const AUTOPILOT_DELAY_MS = 2_500;

/**
 * The most of its clock a bot spends thinking. A bot used a share of the
 * whole clock, so eight bots on a ninety-second clock were ten minutes of
 * waiting a round for the people in the room; a bot that takes a few seconds
 * keeps a draft moving and still reads as somebody choosing.
 */
export const AI_THINK_MAX_MS = 9_000;

export interface DraftPick {
  /** 1-based overall pick number. */
  overall: number;
  round: number;
  /** 1-based position within the round. */
  slot: number;
  teamId: string;
  playerId: string;
  at: number;
  /**
   * Who decided it: 'user' for a live pick, 'ai' for a bot seat, and one of
   * the resolver's own for an autopick — the in-room 'queue', the manager's
   * 'plan', or the house 'rankings'.
   *
   * THE MIDDLE ONE USED TO BE THROWN AWAY HERE. `autopick` mapped everything
   * that was not a queue to 'rankings', so a manager who spent an evening
   * ranking players read 'rankings' beside all fifteen of their picks and was
   * told, wrongly, that their plan had been ignored. Migration `0014` widened
   * the column that had nowhere to put it.
   */
  by: 'user' | 'queue' | 'plan' | 'rankings' | 'ai' | 'keeper';
  /**
   * The account that made it by hand: the manager on the clock, or a
   * commissioner picking for a team. Absent for every automatic pick, and for
   * a host that does not know who is asking.
   */
  madeBy?: string;
  /** What an auction's winner paid (AUCTION.md); absent for a pick. */
  price?: number;
}

export interface DraftState {
  id: string;
  type: DraftType;
  /** Team ids in first-round order. */
  order: string[];
  rounds: number;
  pickSeconds: number;
  status: 'lobby' | 'live' | 'paused' | 'complete';
  picks: DraftPick[];
  /** Per team, player ids in priority order. */
  queues: Record<string, string[]>;
  /** Server time the current pick expires; null unless live. */
  deadlineAt: number | null;
  /**
   * When the turn on the clock began. A seat on auto and a bot time their pick
   * from it, which they once read off the deadline (`deadlineAt − pickSeconds`)
   * — true only while the clock never stops. A league that stops it overnight
   * (C7) has deadlines that skip the night, so the turn keeps its own start.
   * Absent from a state stored before 2026-10-15, which reads the old way.
   */
  turnStartedAt?: number | null;
  /** Seconds left when paused, restored on resume. */
  pausedRemaining: number | null;
  startedAt: number | null;
  completedAt: number | null;
  /** AI-managed seats and their dials. */
  ai: Record<string, AiManager>;
  /** What the clock does at zero (`ClockExpiry`). */
  whenTheClockExpires: ClockExpiry;
  /**
   * Seats drafting by themselves until their manager says otherwise, by team
   * id: a manager who turned auto on, or one whose clock ran out when
   * `autopilotOnExpiry` is set. A bot seat is never here; it has its own.
   */
  autopilot: Record<string, boolean>;
  /**
   * Whether a clock that runs out on a manager who is not in the room puts the
   * seat on auto for the rest of the draft (`DraftContext.isPresent`): a
   * manager who has walked away costs the room one clock, not one a round. The
   * room sets it; the unattended autopick draft leaves it off, since every
   * pick there is the clock's.
   */
  autopilotOnExpiry: boolean;
  /**
   * Whether managers may trade during this draft (`draft.pickTradingAllowed`,
   * DRAFT_ROOM.md "Trading during the draft"). Absent is no, as every state
   * stored before trading read.
   */
  trading?: boolean;
  /**
   * Picks that changed hands, `round:teamId` of the slot's own team → the team
   * that makes it now (`ownerOf`). Absent for a pick nobody traded.
   */
  owners?: Record<string, string>;
  /**
   * The picks that changed hands before the draft, in the league's own trades
   * (PLAN-2026-10-27-the-next-season §2.6 rule 5), keyed as `owners`: where
   * `owners` starts, and what a replay of the room's trades starts from.
   * Absent: none.
   */
  tradedBefore?: Record<string, string>;
  /** Drafted players who changed hands: player id → the team that has him now (`holderOf`). */
  holders?: Record<string, string>;
  /** Offers waiting on an answer, oldest first. */
  offers?: DraftOffer[];
  /** Trades accepted, oldest first. One the commissioner undid is taken off. */
  trades?: DraftTrade[];
  /** The last trade's number, so a trade made after an undo has a new one. */
  tradeSeq?: number;
  /**
   * An auction's own state (AUCTION.md §11): the players up, whose nomination
   * it is, and the rules. Absent for a pick draft. An auction's `type` is
   * `linear`, its nominating order, and its board's.
   */
  auction?: AuctionState;
  /**
   * Last season's players each team kept (PLAN-2026-10-27-the-next-season
   * §2.3): out of the pool from the start. In a pick draft each sits on the
   * team's own pick in `round` and is drafted the moment the clock reaches it
   * (`by: 'keeper'`); in an auction each is the team's at `price` before the
   * first nomination. Absent where nobody keeps anybody.
   */
  keepers?: DraftKeeper[];
  /**
   * A dynasty's carried rosters (PLAN-2026-10-27-the-next-season §2.4 rule
   * 4): the players each team holds from last season, by team id. Out of the
   * pool from the start and counted toward the team's starting needs, so a
   * bot and the autopick draft for what it is missing rather than for an
   * empty roster; none of them takes a pick. Absent where nobody carries
   * anybody.
   */
  held?: Record<string, string[]>;
}

/** A kept player, on a pick his team holds in a round or bought at a price. */
export interface DraftKeeper {
  teamId: string;
  playerId: string;
  /** The round of the pick he takes; absent in an auction. */
  round?: number;
  /**
   * The team whose slot that pick is, when it is another seat's pick his team
   * holds by a trade (§2.6 rule 6). Absent: his team's own.
   */
  slotTeamId?: string;
  /** What he costs from the budget in an auction; absent in a pick draft. */
  price?: number;
}

export type DraftIntent =
  | { type: 'start'; by: string }
  /** `madeBy`: the account that made it by hand, which the room knows and the engine keeps. */
  | { type: 'pick'; teamId: string; playerId: string; madeBy?: string }
  | { type: 'queue.set'; teamId: string; playerIds: string[] }
  | { type: 'pause'; by: string }
  | { type: 'resume'; by: string }
  | { type: 'undo'; by: string }
  | { type: 'clock.set'; by: string; seconds: number }
  | { type: 'autopilot.set'; teamId: string; on: boolean }
  /** A trade put to another team, or a counter to one made to this team (`replaces`). */
  | {
      type: 'trade.offer';
      id: string;
      fromTeamId: string;
      toTeamId: string;
      give: TradeItem[];
      get: TradeItem[];
      replaces?: string;
    }
  | { type: 'trade.answer'; teamId: string; offerId: string; accept: boolean }
  | { type: 'trade.withdraw'; teamId: string; offerId: string }
  | { type: 'trade.undo'; by: string }
  /** An auction's (AUCTION.md §3): put a player up, bid on one, or set the most to bid to. */
  | { type: 'nominate'; teamId: string; playerId: string; bid: number; madeBy?: string }
  | { type: 'bid'; teamId: string; lotId: number; amount: number; madeBy?: string }
  | { type: 'bid.auto'; teamId: string; lotId: number; max: number | null; madeBy?: string };

export type DraftEvent =
  | { type: 'started'; at: number }
  | { type: 'pick.made'; pick: DraftPick }
  | { type: 'turn.changed'; overall: number; teamId: string; deadlineAt: number }
  | { type: 'queue.updated'; teamId: string; playerIds: string[] }
  | { type: 'paused'; by: string; at: number }
  | { type: 'resumed'; by: string; at: number }
  | { type: 'undone'; overall: number; playerId: string }
  | { type: 'clock.set'; seconds: number }
  /** A seat went on auto or came off it: its manager said so, or its clock ran out. */
  | { type: 'autopilot.changed'; teamId: string; on: boolean; reason: 'manager' | 'clock' }
  | { type: 'completed'; at: number }
  /** Offers made, answered, withdrawn or made untrue, for these teams' eyes alone. */
  | {
      type: 'offers.changed';
      teamIds: string[];
      closed: { offer: DraftOffer; reason: OfferEnd }[];
    }
  | { type: 'traded'; trade: DraftTrade }
  | { type: 'trade.undone'; trade: DraftTrade }
  /** An auction's: a player put up, a bid (`auto` when the room made it for an auto-bid), a sale. */
  | { type: 'nominated'; lotId: number; teamId: string; playerId: string; amount: number }
  | { type: 'bid'; lotId: number; teamId: string; amount: number; auto: boolean }
  | { type: 'sold'; lotId: number; pick: DraftPick }
  /** Whose nomination it is now, or nobody's. */
  | { type: 'nominator'; teamId: string | null; deadlineAt: number | null }
  /** A team's auto-bid set or taken off: for that team's eyes alone. */
  | { type: 'auto.set'; teamId: string; lotId: number; max: number | null }
  | {
      type: 'rejected';
      code:
        | 'not-live'
        | 'not-your-turn'
        | 'taken'
        | 'unknown-player'
        | 'unknown-team'
        | 'nothing-to-undo'
        | 'already-started'
        /** A trade that cannot be, whatever happens: unequal, empty, a bot's. */
        | 'bad-trade'
        /** A trade the board has moved past: a pick in it made, a player traded on. */
        | 'stale-trade'
        /** An auction's: more than the team's most, not more than the bid, no spot, sold, ahead. */
        | 'too-much'
        | 'too-little'
        | 'no-room'
        | 'lot-closed'
        | 'ahead';
      message: string;
    };

export interface Applied {
  state: DraftState;
  events: DraftEvent[];
}

/** What the engine needs to know about the player pool and the rosters; the host supplies it. */
export interface DraftContext {
  /** Every draftable player id in ranking order (best first). */
  rankings: readonly string[];
  /** Primary position per player id. */
  positionOf: (playerId: string) => string;
  /** Starter slots: how many of each position a lineup needs (FLEX counted under its eligible positions later). */
  starterNeeds: Readonly<Record<string, number>>;
  /** Hard cap per position (roster.maxPerPosition) or a sensible derived cap. */
  maxPerPosition: Readonly<Record<string, number>>;
  /**
   * The commissioner's caps alone — `roster.maxPerPosition` with nothing
   * derived mixed in. `maxPerPosition` above is what the house list drafts
   * under; this is the ceiling a manager's own plan may not raise (RD3).
   */
  leagueCaps?: Readonly<Record<string, number>>;
  /** Positions that sensible managers leave for the last rounds (kickers, defenses). */
  latePositions: readonly string[];
  /** A deterministic random in [0, 1) for a given key; hosts seed it. */
  random: (key: string) => number;
  /**
   * What each manager submitted, by team id. BACKLOG D2/D3.
   *
   * Absent, or a team missing from it, means that manager never opened a plan
   * — which is not an error and is not schema drift (§4.5). They get the house
   * ranking and the default caps, and they end up with a full plausible team.
   */
  plans?: Readonly<Record<string, ResolvePlan>>;
  /**
   * Whether somebody from this seat is in the room now. The room supplies it
   * so a clock that runs out on a manager who is THERE picks for that turn
   * only, as Sleeper and ESPN do while you are connected, and one that runs
   * out on a manager who has gone leaves the seat on auto, as ESPN, NFL.com
   * and CBS do (DRAFT_ROOM_RESEARCH.md A3). Absent means nobody is.
   */
  isPresent?: (teamId: string) => boolean;
  /**
   * When a clock with `ms` to run, running from `from`, reaches zero: past
   * any time the league has it stopped (C7, the overnight pause). Absent is a
   * clock that never stops.
   */
  clockEnds?: (from: number, ms: number) => number;
  /** How much running time is left from `now` to `deadline`, the inverse of `clockEnds`. */
  clockLeft?: (now: number, deadline: number) => number;
  /**
   * An auction's values in its own money (`auctionValues`, AUCTION.md §7):
   * what a seat on auto bids up to without a price of its own, and where a
   * bot starts.
   */
  auctionValues?: Readonly<Record<string, number>>;
  /**
   * What a bot's draft style reads (AI_MANAGERS.md §4.7, `style.ts`). The
   * league's sport, which picks the part of a character's file that names
   * positions; absent, every bot drafts by the list and its skill alone, as
   * the season suite does.
   */
  sport?: string;
  /** What a style's draws are keyed on: the league's id, so its mock and its draft agree. */
  styleSeed?: string;
  /** What a bot can know about each player: his club, his bye, a rookie, hurt. */
  facts?: Readonly<Record<string, DraftFacts>>;
  /** The sport's clubs by id, with what a style calls them. */
  clubs?: Readonly<Record<string, DraftClub>>;
  /** Each player's worth to this league, the basis of the rankings, for a bot that counts. */
  values?: Readonly<Record<string, number>>;
}

/** The deadline of a clock with `ms` to run from `from`, by the league's clock. */
function endsAt(ctx: DraftContext, from: number, ms: number): number {
  return ctx.clockEnds ? ctx.clockEnds(from, ms) : from + ms;
}

// ── Order ─────────────────────────────────────────────────────────────────

export function currentOverall(state: DraftState): number {
  return state.picks.length + 1;
}

export function currentTeam(state: DraftState): string | null {
  // AN AUCTION HAS NOBODY ON THE CLOCK: it has a nominator and players up.
  if (state.auction) return null;
  if (state.status !== 'live' && state.status !== 'paused') return null;
  if (state.picks.length >= totalPicks(state)) return null;
  // WHOEVER HOLDS THE PICK NOW, which a trade can change (`ownerOf`).
  return ownerOf(state, currentOverall(state));
}

export function isTaken(state: DraftState, playerId: string): boolean {
  return (
    state.picks.some((p) => p.playerId === playerId) ||
    (state.keepers ?? []).some((k) => k.playerId === playerId) ||
    Object.values(state.held ?? {}).some((ids) => ids.includes(playerId))
  );
}

/** The players a team carried into this draft (`DraftState.held`), none in most drafts. */
export function carriedBy(state: DraftState, teamId: string): readonly string[] {
  return state.held?.[teamId] ?? [];
}

/**
 * The players a team has now: those it drafted and kept, and those it got in
 * a trade, with its keepers whose picks are still to come.
 */
export function rosterOf(state: DraftState, teamId: string): string[] {
  const drafted = state.picks.filter((p) => holderOf(state, p) === teamId).map((p) => p.playerId);
  const pending = (state.keepers ?? []).filter(
    (k) => k.teamId === teamId && !drafted.includes(k.playerId),
  );
  return [...drafted, ...pending.map((k) => k.playerId)];
}

/** The keeper on pick `overall` of a pick draft, if one sits there. */
export function keeperAt(state: DraftState, overall: number): DraftKeeper | null {
  if (state.auction || !state.keepers) return null;
  return (
    state.keepers.find(
      (k) =>
        k.round !== undefined && overallOf(state, k.round, k.slotTeamId ?? k.teamId) === overall,
    ) ?? null
  );
}

// ── Create ────────────────────────────────────────────────────────────────

export function createDraft(input: {
  id: string;
  type: DraftType;
  order: string[];
  rounds: number;
  pickSeconds: number;
  ai?: Record<string, AiManager>;
  whenTheClockExpires?: ClockExpiry;
  autopilotOnExpiry?: boolean;
  trading?: boolean;
  /** An auction's rules: a draft with them is an auction, in `order` round and round. */
  auction?: AuctionRules;
  /** Last season's keepers (`DraftState.keepers`). */
  keepers?: readonly DraftKeeper[];
  /** A dynasty's carried rosters (`DraftState.held`). */
  held?: Readonly<Record<string, readonly string[]>>;
  /** The picks that changed hands before the draft (`DraftState.tradedBefore`). */
  owners?: Readonly<Record<string, string>>;
}): DraftState {
  return {
    id: input.id,
    type: input.type,
    order: [...input.order],
    rounds: input.rounds,
    pickSeconds: input.pickSeconds,
    status: 'lobby',
    picks: [],
    queues: Object.fromEntries(input.order.map((t) => [t, []])),
    deadlineAt: null,
    pausedRemaining: null,
    startedAt: null,
    completedAt: null,
    ai: { ...(input.ai ?? {}) },
    whenTheClockExpires: input.whenTheClockExpires ?? 'queue-then-rankings',
    autopilot: {},
    autopilotOnExpiry: input.autopilotOnExpiry ?? false,
    // NO TRADES IN AN AUCTION (AUCTION.md §3.9): there are no picks to trade.
    ...(input.trading && !input.auction ? { trading: true } : {}),
    ...(input.auction ? { auction: auctionState(input.auction) } : {}),
    ...(input.keepers && input.keepers.length > 0
      ? { keepers: input.keepers.map((k) => ({ ...k })) }
      : {}),
    // NO PICKS TO HAVE MOVED IN AN AUCTION (AUCTION.md §3.9).
    ...(input.owners && Object.keys(input.owners).length > 0 && !input.auction
      ? { owners: { ...input.owners }, tradedBefore: { ...input.owners } }
      : {}),
    ...(input.held && Object.values(input.held).some((ids) => ids.length > 0)
      ? {
          held: Object.fromEntries(
            Object.entries(input.held)
              .filter(([, ids]) => ids.length > 0)
              .map(([teamId, ids]) => [teamId, [...ids]]),
          ),
        }
      : {}),
  };
}

// ── Apply ─────────────────────────────────────────────────────────────────

function reject(
  state: DraftState,
  code: Extract<DraftEvent, { type: 'rejected' }>['code'],
  message: string,
): Applied {
  return { state, events: [{ type: 'rejected', code, message }] };
}

/** Apply one intent at time `now`. Never throws; a bad intent yields a `rejected` event. */
export function apply(
  state: DraftState,
  intent: DraftIntent,
  now: number,
  ctx: DraftContext,
): Applied {
  if (state.auction) return applyAuction(state, intent, now, ctx);
  switch (intent.type) {
    case 'start': {
      if (state.status !== 'lobby')
        return reject(state, 'already-started', 'The draft has already started.');
      const next: DraftState = {
        ...state,
        status: 'live',
        startedAt: now,
        deadlineAt: endsAt(ctx, now, state.pickSeconds * 1000),
        turnStartedAt: now,
      };
      const team = currentTeam(next)!;
      const kept = keeperAt(next, 1);
      if (kept !== null) {
        const placed = commitPick(next, kept.playerId, 'keeper', now, ctx);
        return { state: placed.state, events: [{ type: 'started', at: now }, ...placed.events] };
      }
      return {
        state: next,
        events: [
          { type: 'started', at: now },
          { type: 'turn.changed', overall: 1, teamId: team, deadlineAt: next.deadlineAt! },
        ],
      };
    }
    case 'pick': {
      if (state.status !== 'live') return reject(state, 'not-live', 'The draft is not live.');
      const team = currentTeam(state);
      if (team !== intent.teamId) return reject(state, 'not-your-turn', "It's not your pick.");
      if (isTaken(state, intent.playerId))
        return reject(state, 'taken', 'That player is already gone.');
      if (!ctx.rankings.includes(intent.playerId))
        return reject(state, 'unknown-player', 'Unknown player.');
      return commitPick(state, intent.playerId, 'user', now, ctx, intent.madeBy);
    }
    case 'queue.set': {
      // ONLY PLAYERS IN THE POOL. A queue is what an expired clock drafts
      // from, and the pick intent checks the pool where this did not: an id
      // from outside it, queued, became a pick the league could not record.
      const pool = new Set(ctx.rankings);
      const playerIds = intent.playerIds.filter(
        (id, i, a) => a.indexOf(id) === i && pool.has(id) && !isTaken(state, id),
      );
      return {
        state: { ...state, queues: { ...state.queues, [intent.teamId]: playerIds } },
        events: [{ type: 'queue.updated', teamId: intent.teamId, playerIds }],
      };
    }
    case 'pause': {
      if (state.status !== 'live') return reject(state, 'not-live', 'Nothing to pause.');
      // THE RUNNING TIME LEFT, not the wall time to the deadline: a night the
      // league's clock is stopped through is not time the manager had.
      const deadline = state.deadlineAt ?? now;
      const left = ctx.clockLeft ? ctx.clockLeft(now, deadline) : deadline - now;
      const remaining = Math.max(0, left / 1000);
      return {
        state: { ...state, status: 'paused', pausedRemaining: remaining, deadlineAt: null },
        events: [{ type: 'paused', by: intent.by, at: now }],
      };
    }
    case 'resume': {
      if (state.status !== 'paused') return reject(state, 'not-live', 'Nothing to resume.');
      const deadlineAt = endsAt(ctx, now, (state.pausedRemaining ?? state.pickSeconds) * 1000);
      // THE TURN BEGINS AGAIN FROM THE RESUME, as its deadline does: a bot or
      // a seat on auto gets its moment rather than picking the instant the
      // room comes back (the review of B-341).
      const next: DraftState = {
        ...state,
        status: 'live',
        pausedRemaining: null,
        deadlineAt,
        turnStartedAt: now,
      };
      return {
        state: next,
        events: [
          { type: 'resumed', by: intent.by, at: now },
          {
            type: 'turn.changed',
            overall: currentOverall(next),
            teamId: currentTeam(next)!,
            deadlineAt,
          },
        ],
      };
    }
    case 'undo': {
      // A KEEPER'S PICK IS NOBODY'S TO UNDO: it was made by the rules. The
      // undo takes back the last pick somebody made and the keepers' picks
      // after it, which are made again when the clock reaches them.
      const made = state.picks.map((p) => p.by !== 'keeper').lastIndexOf(true);
      if (made < 0) return reject(state, 'nothing-to-undo', 'No pick to undo.');
      const after = state.picks.slice(made + 1);
      const last = state.picks[made]!;
      // A PLAYER A TRADE MOVED stays drafted until the trade is undone first:
      // undrafted, the trade would name somebody nobody has.
      const traded = (state.trades ?? []).some((t) =>
        [...t.give, ...t.get].some((i) => i.kind === 'player' && i.playerId === last.playerId),
      );
      if (traded) {
        return reject(
          state,
          'stale-trade',
          'He has been traded since that pick. Undo the trade first.',
        );
      }
      const picks = state.picks.slice(0, made);
      const next: DraftState = {
        ...state,
        picks,
        status: state.status === 'complete' ? 'live' : state.status,
        completedAt: null,
        deadlineAt: state.status === 'paused' ? null : endsAt(ctx, now, state.pickSeconds * 1000),
        // THE TURN BEGINS AGAIN: an undone pick's team is on a fresh clock.
        turnStartedAt: now,
      };
      const events: DraftEvent[] = [
        ...after
          .reverse()
          .map((p) => ({ type: 'undone' as const, overall: p.overall, playerId: p.playerId })),
        { type: 'undone', overall: last.overall, playerId: last.playerId },
      ];
      if (next.status === 'live')
        events.push({
          type: 'turn.changed',
          overall: currentOverall(next),
          teamId: currentTeam(next)!,
          deadlineAt: next.deadlineAt!,
        });
      // AN OFFER NAMING HIM is untrue now that nobody has him.
      const swept = sweepOffers(next);
      return { state: swept.state, events: [...events, ...swept.events] };
    }
    case 'clock.set': {
      const seconds = Math.max(15, Math.round(intent.seconds));
      const next: DraftState = {
        ...state,
        pickSeconds: seconds,
        deadlineAt: state.status === 'live' ? endsAt(ctx, now, seconds * 1000) : state.deadlineAt,
        // A new clock is a fresh turn's, as it always read.
        turnStartedAt: state.status === 'live' ? now : state.turnStartedAt,
      };
      return { state: next, events: [{ type: 'clock.set', seconds }] };
    }
    case 'autopilot.set': {
      if (!state.order.includes(intent.teamId))
        return reject(state, 'unknown-team', 'That team is not in this draft.');
      // A BOT SEAT HAS ITS OWN WAY OF PICKING and is never on auto: the room
      // would otherwise draft a bot's picks from the house list rather than
      // in its own style.
      if (state.ai[intent.teamId])
        return reject(state, 'unknown-team', 'A bot drafts that seat itself.');
      const autopilot = { ...state.autopilot };
      if (intent.on) autopilot[intent.teamId] = true;
      else delete autopilot[intent.teamId];
      const turned: Applied = {
        state: { ...state, autopilot },
        events: [
          { type: 'autopilot.changed', teamId: intent.teamId, on: intent.on, reason: 'manager' },
        ],
      };
      // ON WHILE ON THE CLOCK IS A PICK NOW. Somebody who turns auto on with
      // their own clock running is saying "take it for me", and making them
      // watch two and a half more seconds of it would read as the switch not
      // working.
      if (intent.on && state.status === 'live' && currentTeam(state) === intent.teamId) {
        const picked = autopick(turned.state, now, ctx);
        return { state: picked.state, events: [...turned.events, ...picked.events] };
      }
      return turned;
    }
    case 'trade.offer':
      return offerTrade(state, intent, now);
    case 'trade.answer':
      return answerOffer(state, intent, now);
    case 'trade.withdraw':
      return withdrawOffer(state, intent);
    case 'trade.undo':
      return undoTrade(state, now);
    case 'nominate':
    case 'bid':
    case 'bid.auto':
      return reject(state, 'not-live', 'This draft is not an auction.');
  }
}

/** The instant a seat's current turn began, or null when nobody is on the clock. */
function turnStartedAt(state: DraftState): number | null {
  if (state.deadlineAt === null) return null;
  return state.turnStartedAt ?? state.deadlineAt - state.pickSeconds * 1000;
}

/** When a seat on auto picks: a moment after its turn starts (`AUTOPILOT_DELAY_MS`). */
function autopilotAt(state: DraftState, teamId: string): number | null {
  if (!state.autopilot[teamId] || state.ai[teamId]) return null;
  const start = turnStartedAt(state);
  if (start === null) return null;
  return start + Math.min(AUTOPILOT_DELAY_MS, state.pickSeconds * 1000);
}

function commitPick(
  state: DraftState,
  playerId: string,
  by: DraftPick['by'],
  now: number,
  ctx: DraftContext,
  madeBy?: string,
): Applied {
  const overall = currentOverall(state);
  const n = state.order.length;
  const pick: DraftPick = {
    overall,
    round: Math.ceil(overall / n),
    slot: ((overall - 1) % n) + 1,
    teamId: currentTeam(state)!,
    playerId,
    at: now,
    by,
    ...(madeBy === undefined ? {} : { madeBy }),
  };
  // The drafted player leaves every queue.
  const queues = Object.fromEntries(
    Object.entries(state.queues).map(([t, ids]) => [t, ids.filter((id) => id !== playerId)]),
  );
  const picks = [...state.picks, pick];
  const done = picks.length >= totalPicks(state);
  const next: DraftState = {
    ...state,
    picks,
    queues,
    status: done ? 'complete' : 'live',
    deadlineAt: done ? null : endsAt(ctx, now, state.pickSeconds * 1000),
    turnStartedAt: done ? null : now,
    completedAt: done ? now : null,
  };
  const events: DraftEvent[] = [{ type: 'pick.made', pick }];
  if (done) events.push({ type: 'completed', at: now });
  else
    events.push({
      type: 'turn.changed',
      overall: overall + 1,
      teamId: currentTeam(next)!,
      deadlineAt: next.deadlineAt!,
    });
  // AN OFFER NAMING THE PICK JUST MADE is void, and its two teams are told;
  // the last pick ends every offer, since trading ends with the draft.
  const swept = done ? endOffers(next) : sweepOffers(next);
  // A KEEPER ON THE NEXT PICK is drafted at once, by the rules: nobody is on
  // the clock for a pick a keeper already has (§2.3 rule 7).
  const kept = done ? null : keeperAt(swept.state, overall + 1);
  if (kept !== null) {
    const placed = commitPick(swept.state, kept.playerId, 'keeper', now, ctx);
    return {
      state: placed.state,
      events: [
        ...events.filter((e) => e.type !== 'turn.changed'),
        ...swept.events,
        ...placed.events,
      ],
    };
  }
  return { state: swept.state, events: [...events, ...swept.events] };
}

/** Every offer still waiting, ended: the draft is over. */
function endOffers(state: DraftState): Applied {
  const offers = state.offers ?? [];
  if (offers.length === 0) return { state, events: [] };
  return {
    state: { ...state, offers: [] },
    events: [
      {
        type: 'offers.changed',
        teamIds: [...new Set(offers.flatMap((o) => [o.fromTeamId, o.toTeamId]))],
        closed: offers.map((offer) => ({ offer, reason: 'void' as const })),
      },
    ],
  };
}

// ── Autopick and AI ───────────────────────────────────────────────────────

/** Positions this team still needs for its starting lineup. */
export function needs(
  state: DraftState,
  teamId: string,
  ctx: DraftContext,
): Record<string, number> {
  const have: Record<string, number> = {};
  // WHAT IT CARRIED COUNTS (§2.4 rule 4): a dynasty's team with every starter
  // drafts rookies for their worth, not a quarterback it has.
  for (const id of [...carriedBy(state, teamId), ...rosterOf(state, teamId)]) {
    const pos = ctx.positionOf(id);
    have[pos] = (have[pos] ?? 0) + 1;
  }
  const out: Record<string, number> = {};
  for (const [pos, count] of Object.entries(ctx.starterNeeds)) {
    const missing = count - (have[pos] ?? 0);
    if (missing > 0) out[pos] = missing;
  }
  return out;
}

function underCap(state: DraftState, teamId: string, playerId: string, ctx: DraftContext): boolean {
  const pos = ctx.positionOf(playerId);
  if (atLeagueCap(state, teamId, pos, ctx)) return false;
  const cap = ctx.maxPerPosition[pos];
  if (cap === undefined) return true;
  return rosterOf(state, teamId).filter((id) => ctx.positionOf(id) === pos).length < cap;
}

/**
 * Whether a team holds all the league's own cap allows at a position
 * (`leagueCaps`, the rules' `maxPerPosition`), counting what it carried into a
 * dynasty's draft (`DraftState.held`) as well as its picks: a player past the
 * cap is one it would have to let go (PLAN-2026-10-27-the-next-season §2.4).
 * The caps the draft derives for itself count its picks alone.
 */
export function atLeagueCap(
  state: DraftState,
  teamId: string,
  position: string,
  ctx: DraftContext,
): boolean {
  const cap = ctx.leagueCaps?.[position];
  if (cap === undefined) return false;
  return (
    [...carriedBy(state, teamId), ...rosterOf(state, teamId)].filter(
      (id) => ctx.positionOf(id) === position,
    ).length >= cap
  );
}

/**
 * The picks a team has left to make: those still to come that it holds, a
 * trade's included, less any a keeper of its sits on, whose player is its
 * already. Without trades the rounds it has left less its keepers still to
 * come; a pick traded away before the draft or in the room is one fewer, and
 * one traded for is one more (the review of chunk 5,
 * PLAN-2026-10-27-the-next-season §2.6). An auction has no picks: its rounds
 * less its roster.
 */
export function picksLeft(state: DraftState, teamId: string): number {
  if (state.auction) return state.rounds - rosterOf(state, teamId).length;
  let left = 0;
  for (let overall = state.picks.length + 1; overall <= totalPicks(state); overall += 1)
    if (ownerOf(state, overall) === teamId && keeperAt(state, overall) === null) left += 1;
  return left;
}

/** The best available by rankings, honoring caps and leaving late positions for the last rounds. */
export function bestAvailable(state: DraftState, teamId: string, ctx: DraftContext): string | null {
  // THE PICKS THIS TEAM HAS LEFT (`picksLeft`), which a trade can make more
  // or fewer than the rounds left.
  const roundsLeft = picksLeft(state, teamId);
  const need = needs(state, teamId, ctx);
  const lateNeeded = ctx.latePositions.filter((p) => need[p]).length;
  const saveLate = roundsLeft > lateNeeded;
  const available = ctx.rankings.filter(
    (id) => !isTaken(state, id) && underCap(state, teamId, id, ctx),
  );
  const late = available.filter((id) => ctx.latePositions.includes(ctx.positionOf(id)));
  const early = available.filter((id) => !ctx.latePositions.includes(ctx.positionOf(id)));
  // Once the rounds left are down to the starters still missing, fill needs first.
  const needCount = Object.values(need).reduce((a, b) => a + b, 0);
  const mustFill = roundsLeft <= needCount;
  if (!saveLate) {
    const needed = late.find((id) => need[ctx.positionOf(id)]);
    if (needed) return needed;
  }
  if (mustFill) {
    const needed = (saveLate ? early : available).find((id) => need[ctx.positionOf(id)]);
    if (needed) return needed;
  }
  const pool = saveLate ? early : available;
  return pool[0] ?? available[0] ?? null;
}

/**
 * Autopick for the team on the clock. BACKLOG D3.
 *
 * THIS USED TO BE QUEUE-THEN-RANKINGS, with no idea a manager could have a
 * plan. `resolvePick` is the whole of §4.6's precedence — queue, then the
 * manager's plan, then the house — with §4.2's caps and the exclusion list
 * applied at every level, and it carries out WHICH of the three acted so the
 * recap can say so rather than leaving somebody to guess.
 *
 * The old `bestAvailable` is still the house ranking's own behavior and is
 * still exported: it is what fills `house` below, and it is what a draft with
 * no plans at all has always done.
 */
export function autopick(
  state: DraftState,
  now: number,
  ctx: DraftContext,
  options: { queue?: boolean } = {},
): Applied {
  const team = currentTeam(state);
  if (!team || state.status !== 'live') return { state, events: [] };

  // `rankings` on an expired clock skips the queue (`ClockExpiry`).
  const { resolved } = resolveFor(
    state,
    team,
    ctx,
    options.queue === false ? [] : (state.queues[team] ?? []),
  );
  if (resolved.playerId === null) return { state, events: [] };
  // WHICH OF THE THREE ACTED CARRIES THROUGH. `resolved.by` is 'none' only
  // when there is nobody to take, which is the null above; the rest map one
  // to one onto the column, with the resolver's 'house' called 'rankings'
  // because that is the word the board and the recap have always used.
  const by = resolved.by === 'house' || resolved.by === 'none' ? 'rankings' : resolved.by;
  return commitPick(state, resolved.playerId, by, now, ctx);
}

/**
 * Who this team's plan takes now, and the question it was asked: the queue,
 * the plan, then the house (`resolvePick`), as `autopick` asks it of the
 * team on the clock. Exported for the replay (C11), which asks it of a team
 * that is not on the clock, to say which pick took the name its list had next.
 */
export function resolveFor(
  state: DraftState,
  team: string,
  ctx: DraftContext,
  queue: readonly string[],
): { resolved: Resolved; input: ResolveInput } {
  const roster = rosterOf(state, team);
  // THE PLAYERS IT HAS, not the picks it made: a trade can move either.
  const picksTaken = roster.length;
  // AND WHAT IT CARRIED INTO A DYNASTY'S DRAFT, which its plan's fewest and
  // the league's caps count (§2.4 rule 4), but which takes no pick. A pick
  // past a cap rather than none: the team lets one go after.
  const carried = carriedBy(state, team);
  const inputFor = (counted: readonly string[]): ResolveInput => ({
    plan: ctx.plans?.[team] ?? null,
    queue,
    // `bestAvailable` first, so the house list this team sees already honors
    // the league's own caps and the leave-kickers-late habit; the full ranking
    // follows it as the tail, because unranked is not excluded (§4.5).
    house: dedupe([bestAvailable(state, team, ctx), ...ctx.rankings]),
    // A KEEPER STILL TO BE PLACED is taken already (§2.3): an autopick that
    // drafted him would draft him twice when his pick came.
    taken: new Set([
      ...state.picks.map((p) => p.playerId),
      ...(state.keepers ?? []).map((k) => k.playerId),
      // A CARRIED PLAYER is his team's already (§2.4 rule 4).
      ...Object.values(state.held ?? {}).flat(),
    ]),
    roster: counted,
    round: Math.floor(picksTaken) + 1,
    picksLeft: state.rounds - picksTaken,
    positionOf: ctx.positionOf,
    caps: ctx.leagueCaps,
  });
  let input = inputFor([...carried, ...roster]);
  let resolved = resolvePick(input);
  if (resolved.playerId === null && carried.length > 0) {
    input = inputFor(roster);
    resolved = resolvePick(input);
  }
  return { resolved, input };
}

/** First occurrence wins; nulls dropped. */
function dedupe(ids: readonly (string | null)[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (id === null || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * When an AI seat will pick, in server time: a share of its clock that
 * depends on its activity dial, never the whole clock. Deterministic per pick.
 */
export function aiPickAt(state: DraftState, teamId: string, ctx: DraftContext): number | null {
  const ai = state.ai[teamId];
  if (!ai || state.deadlineAt === null) return null;
  const overall = currentOverall(state);
  const share =
    0.15 + (1 - ai.dials.activity) * 0.55 + ctx.random(`${state.id}:think:${overall}`) * 0.2;
  const start = turnStartedAt(state)!;
  // A SHARE OF A FEW SECONDS, NOT OF THE WHOLE CLOCK (`AI_THINK_MAX_MS`).
  const span = Math.min(state.pickSeconds * 1000, AI_THINK_MAX_MS);
  return Math.round(start + share * span);
}

/**
 * When the room next has something to do by itself: a seat on auto picking,
 * a bot picking, or a clock running out. Null when nothing will happen until
 * somebody acts — a draft not live, or a clock at zero under the soft timer.
 * A host sets its one alarm here (REALTIME.md: an alarm, never a timer).
 */
export function nextAt(state: DraftState, ctx: DraftContext): number | null {
  if (state.auction) return auctionNextAt(state, ctx);
  if (state.status !== 'live' || state.deadlineAt === null) return null;
  const team = currentTeam(state);
  if (!team) return null;
  const due: number[] = [];
  const auto = autopilotAt(state, team);
  if (auto !== null) due.push(auto);
  const think = aiPickAt(state, team, ctx);
  if (think !== null) due.push(think);
  if (state.whenTheClockExpires !== 'off') due.push(state.deadlineAt);
  return due.length === 0 ? null : Math.min(...due);
}

/**
 * Move the draft forward to `now`: seats on auto pick a moment into their
 * turn, AI seats pick when their think time passes, and anyone else is
 * picked for when their clock expires — unless the league's clock is the
 * soft one, which waits. Loops until the state is caught up. Hosts call this
 * on every tick or alarm.
 */
export function advance(state: DraftState, now: number, ctx: DraftContext): Applied {
  if (state.auction) return advanceAuction(state, now, ctx);
  let cur = state;
  const events: DraftEvent[] = [];
  for (let guard = 0; guard < totalPicks(state) + 1; guard++) {
    if (cur.status !== 'live' || cur.deadlineAt === null) break;
    const team = currentTeam(cur);
    if (!team) break;
    const autoAt = autopilotAt(cur, team);
    if (autoAt !== null && autoAt <= now) {
      const r = autopick(cur, autoAt, ctx);
      if (r.events.length === 0) break;
      cur = r.state;
      events.push(...r.events);
      continue;
    }
    const thinkAt = aiPickAt(cur, team, ctx);
    if (thinkAt !== null && thinkAt <= now) {
      const playerId = pickAi(cur, team, ctx);
      const r = playerId
        ? commitPick(cur, playerId, 'ai', thinkAt, ctx)
        : autopick(cur, thinkAt, ctx);
      // A BOT WITH NOBODY IT MAY TAKE stops here rather than going round.
      if (r.events.length === 0) break;
      cur = r.state;
      events.push(...r.events);
      continue;
    }
    if (cur.deadlineAt <= now) {
      // THE SOFT TIMER: the clock reads zero and the room waits. Nothing is
      // picked for a manager who chose a league where nobody is.
      if (cur.whenTheClockExpires === 'off') break;
      const r = autopick(cur, cur.deadlineAt, ctx, {
        queue: cur.whenTheClockExpires !== 'rankings',
      });
      if (r.events.length === 0) break;
      cur = r.state;
      events.push(...r.events);
      // AND A SEAT NOBODY IS SITTING IN STAYS ON AUTO until its manager is
      // back and says so, where the host asked for it (`autopilotOnExpiry`).
      // A manager who is in the room and slow loses this pick and no more.
      if (
        cur.autopilotOnExpiry &&
        !cur.ai[team] &&
        !cur.autopilot[team] &&
        !(ctx.isPresent?.(team) ?? false)
      ) {
        cur = { ...cur, autopilot: { ...cur.autopilot, [team]: true } };
        events.push({ type: 'autopilot.changed', teamId: team, on: true, reason: 'clock' });
      }
      continue;
    }
    break;
  }
  return { state: cur, events };
}
